// Disables console message: Download the Apollo DevTools for a better development experience
import { getEnvVariables } from 'src/utils/envConfig';
import { ApolloClient, ApolloLink, from, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import ApolloLinkTimeout from 'apollo-link-timeout';
import LS from 'src/utils/LS';
import { getApiToken, isAuth0Session } from 'src/utils/tokenProvider';
import { clearJwt } from 'src/utils/jwt';
import cacheOptions from 'src/startup/apollo/cache-options';
import dereferenceDocument, { canDerefence } from 'src/utils/dereferenceDocument';
import _ from 'lodash';
import { Kind } from 'graphql';
// eslint-disable-next-line no-restricted-imports
import { API_TRACKING_HEADER, getSetupOptions } from '@pi/api-client';

import type { HttpOptions, ServerError } from '@apollo/client';
import type { Maybe } from 'src/generated/graphql';

export type OriginalUserData = { token: string; displayName: string } | null;

const env = getEnvVariables();
const API_URL = env.apiV2Url! + '/graphql';

// Async because an Auth0 session resolves its token through the SDK, which may refresh it.
// Note `...headers` is spread last on purpose: it lets a caller pass `context: { headers: { jwt: '' } }`
// to make a deliberately unauthenticated request.
const authLink = setContext(async (_, { headers }) => {
    return {
        headers: {
            jwt: await getApiToken(),
            [API_TRACKING_HEADER]: JSON.stringify(getSetupOptions().tracking),
            ...headers,
        },
    };
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const { noErrorAlert } = operation.getContext();

    // Deliberately outside the noErrorAlert guard below: that flag only silences console noise, and
    // a caller opting out of logging must not also opt out of dropping a credential the server just
    // rejected - that is how a session gets stuck retrying a dead token.
    if ((networkError as ServerError)?.statusCode === 401) {
        if (isAuth0Session()) {
            // The Auth0 token *is* the credential, so a 401 means it is no longer usable and could
            // not be refreshed. Clearing the marker drops the app back to the login screen.
            clearJwt();
        } else {
            LS.set({ jwt: '' });
        }
    }

    if (!noErrorAlert) {
        if (graphQLErrors) {
            // eslint-disable-next-line no-console
            graphQLErrors.forEach(err => console.error(err));
        }

        if (networkError) {
            // eslint-disable-next-line no-console
            console.error(networkError);
        }
    }
});

const getOperationOptions = (
    requestInit: RequestInit,
): {
    operationName?: string;
    query?: string;
} => {
    let operations: Maybe<string> = null;

    if (requestInit.body) {
        if (requestInit.body instanceof FormData) {
            const object: any = {};

            requestInit.body.forEach((value, key) => {
                object[key] = value;
            });

            operations = object.operations;
        } else {
            operations = requestInit.body.toString();
        }
    }

    if (!operations) return {};

    const { operationName, query } = JSON.parse(operations);

    return { operationName, query };
};

const separatedFetch: HttpOptions['fetch'] = (_str, init) => {
    let fetchUri: string = API_URL;

    if (init) {
        const { operationName, query } = getOperationOptions(init);

        // Add the operation + type name at the end of each graphql fetch operation for easier network inspect
        if (operationName && query) {
            try {
                const match = query.match(/query|mutation/);
                const suffix: string = match?.[0] ? `:${match[0]}` : '';

                fetchUri = `${API_URL}/${operationName}${suffix}`;
            } catch (ex) {
                // noop
            }
        }
    }

    return fetch(fetchUri, init);
};

const httpLink = createUploadLink({
    fetch: separatedFetch,
    // No jwt header here: it was resolved once at module init and always overwritten by authLink,
    // which is now the single place the credential is read (and is async, for Auth0 sessions).
});

const dereferenceLink = new ApolloLink((operation, forward) => {
    const def = operation.query.definitions[0];

    /**
     * Automatically converts references to their _id values to match standard create/update mutations
     * You can opt out derefencing via the doNotDereference() method;
     */
    if (def?.kind === Kind.OPERATION_DEFINITION && def.operation === 'mutation') {
        if (canDerefence(operation.variables)) {
            operation.variables = _.mapValues(operation.variables, dereferenceDocument);
        }
    }

    return forward(operation);
});

const timeoutLink = new ApolloLinkTimeout(10 * 60e3);
const linkWithTimeout = timeoutLink.concat(httpLink) as any;
const link = from([authLink, errorLink, dereferenceLink, linkWithTimeout]);

const cache = new InMemoryCache({
    ...cacheOptions,
});

const apolloClient = new ApolloClient({
    cache,
    link,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
        query: {
            fetchPolicy: 'network-only',
        },
        mutate: {
            fetchPolicy: 'network-only',
        },
    },
});

export default apolloClient;
